import React, { FC } from 'react';

import { Box, Dialog, DialogContent } from '@mui/material';
import * as Yup from 'yup';

import ModalContent from 'src/components/ModalContent/ModalContent';
import ReusableGrid from 'src/components/ReusableGrid';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useCreateNote } from 'src/hooks';
import useMerchantDashboardNotes from 'src/hooks/useMerchantDashboardNotes/useMerchantDashboardNotes';
import Notes from 'src/pages/ViewPartnerPage/components/Notes/Notes';

type NotesSectionProps = {
    merchantId?: number;
};

type Note = {
    id: number;
    subject: string;
    createdAt: string;
    body: string;
    createdBy: { id: number; firstName: string; lastName: string };
};

const createNoteValidationSchema = Yup.object().shape({
    subject: Yup.string().trim().min(3).required('Subject is required'),
    description: Yup.string().nullable().trim(),
});

const NotesSection: FC<NotesSectionProps> = ({ merchantId = 0 }) => {
    const [openCreateNoteModal, setOpenCreateNoteModal] = React.useState(false);
    const { data, refetchNotes } = useMerchantDashboardNotes(merchantId ?? 0);

    const { onCreateNote } = useCreateNote({ merchantId });

    const handleModalOpenCloseState = () => {
        setOpenCreateNoteModal((prevState) => {
            return !prevState;
        });
    };

    const handleCreateNote = async (values: { description: string; subject: string }) => {
        try {
            await onCreateNote({ description: values?.description, subject: values?.subject });

            await refetchNotes();
        } catch (e) {
            showErrorToast(e?.message);
        } finally {
            handleModalOpenCloseState();
        }
    };

    const notes = Array.from(data?.merchantDashboard?.notes ?? []).map((item: Note) => {
        return {
            id: item?.id,
            subject: item?.subject,
            body: item?.body,
            createdAt: item?.createdAt,
            createdBy: {
                id: item?.createdBy?.id,
                firstName: item?.createdBy?.firstName,
                lastName: item?.createdBy?.lastName,
            },
        };
    });

    return (
        <>
            <Box
                mt={4}
                width="100%"
                data-testid="notes-section"
                sx={{
                    '& .MuiDataGrid-toolbarContainer': { display: 'none' },
                }}
            >
                <ReusableGrid
                    title="Notes"
                    xs={12}
                    addRecordButtonConfig={{
                        addRecordButtonText: 'Add Note',
                        addRecordCallBack: handleModalOpenCloseState,
                    }}
                >
                    <Notes notes={notes} refetch={() => {}} isMerchant={false} hideDelete />
                </ReusableGrid>
            </Box>

            <Dialog
                open={openCreateNoteModal}
                onClose={handleModalOpenCloseState}
                aria-labelledby="create-note-offer-dialog"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleModalOpenCloseState}
                        buttonText="Create Note"
                        header="Create Note"
                        initialValues={{
                            description: '',
                            subject: '',
                        }}
                        handleFormSubmit={handleCreateNote}
                        isSubmitModal
                        handleAction={() => {}}
                        message=""
                        validationSchema={createNoteValidationSchema}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default NotesSection;
